import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { YouTubePlayer } from 'react-youtube';

export interface VideoPlaybackRegistry {
  register: (elementId: string, player: YouTubePlayer) => void;
  unregister: (elementId: string, player?: YouTubePlayer) => void;
  hasPlayer: (elementId: string) => boolean;
  getCurrentTime: (elementId: string) => Promise<number | null>;
  pauseAll: () => Promise<void>;
}

const noopRegistry: VideoPlaybackRegistry = {
  register: () => {},
  unregister: () => {},
  hasPlayer: () => false,
  getCurrentTime: () => Promise.resolve(null),
  pauseAll: () => Promise.resolve(),
};

const VideoPlaybackRegistryContext = createContext<VideoPlaybackRegistry>(noopRegistry);

export function useCreateVideoPlaybackRegistry(): VideoPlaybackRegistry {
  const playersRef = useRef<Map<string, YouTubePlayer>>(new Map());
  const [activePlayerIds, setActivePlayerIds] = useState<ReadonlySet<string>>(() => new Set());

  const register = useCallback((elementId: string, player: YouTubePlayer) => {
    playersRef.current.set(elementId, player);
    setActivePlayerIds((prev) => {
      if (prev.has(elementId)) return prev;
      const next = new Set(prev);
      next.add(elementId);
      return next;
    });
  }, []);

  const unregister = useCallback((elementId: string, player?: YouTubePlayer) => {
    const currentPlayer = playersRef.current.get(elementId);
    if (!currentPlayer) return;
    if (player && currentPlayer !== player) return;

    playersRef.current.delete(elementId);
    setActivePlayerIds((prev) => {
      if (!prev.has(elementId)) return prev;
      const next = new Set(prev);
      next.delete(elementId);
      return next;
    });
  }, []);

  const hasPlayer = useCallback(
    (elementId: string) => activePlayerIds.has(elementId),
    [activePlayerIds]
  );

  const getCurrentTime = useCallback(async (elementId: string): Promise<number | null> => {
    const player = playersRef.current.get(elementId);
    if (!player) return null;

    try {
      const currentTime = await Promise.resolve(player.getCurrentTime());
      return typeof currentTime === 'number' && Number.isFinite(currentTime) ? currentTime : null;
    } catch {
      return null;
    }
  }, []);

  const pauseAll = useCallback(async (): Promise<void> => {
    const players = Array.from(playersRef.current.values());
    await Promise.all(
      players.map(async (player) => {
        try {
          await Promise.resolve(player.pauseVideo());
        } catch {
          // Ignore stale or already-destroyed players.
        }
      })
    );
  }, []);

  return useMemo(
    () => ({ register, unregister, hasPlayer, getCurrentTime, pauseAll }),
    [register, unregister, hasPlayer, getCurrentTime, pauseAll]
  );
}

interface VideoPlaybackRegistryProviderProps {
  children: ReactNode;
  value: VideoPlaybackRegistry;
}

export function VideoPlaybackRegistryProvider({
  children,
  value,
}: VideoPlaybackRegistryProviderProps) {
  return (
    <VideoPlaybackRegistryContext.Provider value={value}>
      {children}
    </VideoPlaybackRegistryContext.Provider>
  );
}

export function useVideoPlaybackRegistry(): VideoPlaybackRegistry {
  return useContext(VideoPlaybackRegistryContext);
}
